/**
 * RUTAS
 *
 * Todo lo que no sea `/entrar` exige sesión. `RequiereSesion` es el único
 * sitio donde se decide eso, y no es una barrera de seguridad —la barrera
 * está en la API—: es lo que evita enseñar un esqueleto vacío a quien no
 * ha entrado.
 */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Cargando, Error as ErrorCaja } from './componentes/base.tsx';
import { EstadoConexion } from './componentes/estado-conexion.tsx';
import { useSesion } from './contexto.tsx';
import { PaginaActivo, PaginaActivos, PaginaEtiquetaActivo, PaginaTiposActivo } from './paginas/activos.tsx';
import { PaginaIncidenciaMnt, PaginaIncidenciasMnt, PaginaNuevaIncidenciaMnt } from './paginas/incidencias-mnt.tsx';
import { PaginaNuevoParte, PaginaParte, PaginaPartes } from './paginas/partes.tsx';
import { PaginaAlmacen, PaginaAlmacenes, PaginaArticulo, PaginaArticulos, PaginaMaestrosInventario } from './paginas/inventario.tsx';
import { PaginaMovimientos, PaginaNuevaOperacion, PaginaOperacion, PaginaStock } from './paginas/stock.tsx';
import { PaginaInventarioFisico, PaginaInventariosFisicos } from './paginas/inventarios-fisicos.tsx';
import { PaginaImportacionMaestra, PaginaImportacionesMaestras, PaginaNuevaImportacionMaestra } from './paginas/importaciones-maestras.tsx';
import { PaginaEscaneosPendientes } from './paginas/escaneos-pendientes.tsx';
import { PaginaExportacion, PaginaExportaciones, PaginaNuevaExportacion } from './paginas/exportaciones.tsx';
import { PaginaCierre, PaginaCierres, PaginaConciliacion, PaginaKardex, PaginaStockHistorico } from './paginas/historico.tsx';
import { PaginaAlmacenesMoviles, PaginaAlmacenMovil, PaginaMiFurgoneta } from './paginas/almacenes-moviles.tsx';
import { PaginaLote, PaginaSerie } from './paginas/trazabilidad.tsx';
import { PaginaPlannerMantenimiento } from './paginas/planner-mantenimiento.tsx';
import { PaginaIndicadoresMantenimiento } from './paginas/indicadores-mantenimiento.tsx';
import { PaginaPortalIndicadoresMnt } from './paginas/portal-indicadores-mantenimiento.tsx';
import {
  PaginaPortalActivo,
  PaginaPortalActivos,
  PaginaPortalEtiquetaActivo,
  PaginaPortalIncidenciaMnt,
  PaginaPortalIncidenciasMnt,
  PaginaPortalMantenimiento,
  PaginaPortalNuevaIncidenciaMnt,
  PaginaPortalParte,
  PaginaPortalPartes,
  PaginaPortalProximos,
} from './paginas/portal-mantenimiento.tsx';
import { PaginaPreventivo, PaginaPreventivos } from './paginas/preventivos.tsx';
import { PaginaCalidad, PaginaIncidencia, PaginaMisAcciones, PaginaNcCalidad, PaginaReportarIncidencia } from './paginas/calidad.tsx';
import { PaginaCambiarPassword } from './paginas/cambiar-password.tsx';
import { DisposicionCentro, PaginaCalendario, PaginaCapacidadCarga, ResumenCentro } from './paginas/centro.tsx';
import { PaginaCliente, PaginaClientes } from './paginas/clientes.tsx';
import { PaginaIndicadoresPortal } from './paginas/indicadores-portal.tsx';
import {
  DisposicionTipoLimpieza,
  PaginaTiposLimpieza,
  TabDatosTipoLimpieza,
  TabRequisitosTipoLimpieza,
} from './paginas/tipos-limpieza.tsx';
import { PaginaImportaciones, PaginaNuevaImportacion } from './paginas/importacion.tsx';
import { PaginaInicio } from './paginas/inicio.tsx';
import { PaginaLogin } from './paginas/login.tsx';
import { PaginaOt, PaginaOts, PaginaOtsGlobal } from './paginas/ots.tsx';
import { PaginaPlanner } from './paginas/planner.tsx';
import {
  DisposicionOperario,
  PaginaOperarios,
  TabAptitudOperario,
  TabCentrosOperario,
  TabDatosOperario,
  TabDisponibilidadOperario,
  TabPolivalenciasOperario,
} from './paginas/operarios.tsx';
import { PaginaEditorPlan } from './paginas/editor-plan.tsx';
import { PaginaDocumentos } from './paginas/documentos.tsx';
import { PaginaPlantillasCalendario } from './paginas/plantillas-calendario.tsx';
import { MisTrabajosSinSesion, RutaMiOt, RutaMisOts } from './paginas/mis-trabajos.tsx';
import { MisPartesSinSesion, PaginaMisPartes, PaginaParteCampo } from './paginas/mis-partes-campo.tsx';
import { PaginaPortalCalidad, PaginaPortalIncidencia, PaginaPortalNc, PaginaPortalNuevaNc } from './paginas/portal-calidad.tsx';
import { PaginaPortalPlanner } from './paginas/portal-planner.tsx';
import { PaginaOffline, PaginaOfflineOt } from './paginas/offline.tsx';
import { PuertaOfflineSegura } from './paginas/offline-perfil.tsx';
import { PaginaOfflineRevision } from './paginas/offline-revision.tsx';
import { PaginaPlanes, PaginaPlanificacion } from './paginas/planificacion.tsx';
import { PaginaPolivalencias } from './paginas/polivalencias.tsx';
import { PaginaUbicaciones } from './paginas/ubicaciones.tsx';
import { PaginaUsuario, PaginaUsuarios } from './paginas/usuarios.tsx';
import { useConectividad } from './pwa.ts';

const RUTA_CAMBIAR_PASSWORD = '/cambiar-password';

/**
 * Sesión + guardia de contraseña temporal.
 *
 * `requiereCambioPwd` es la MISMA guardia que aplica el backend en
 * `autenticar()` — servidor.ts, allowlist de tres rutas —: aquí no es la
 * barrera de seguridad (esa es la de la API, que rechaza con
 * CAMBIO_PASSWORD_REQUERIDO cualquier otra llamada), es sólo evitar que
 * la UI ofrezca una navegación que la API va a rechazar. Por eso la
 * única ruta que esta guardia deja pasar sin redirigir es
 * `/cambiar-password` — el resto de la aplicación entera queda fuera
 * mientras la contraseña temporal siga activa.
 */
/**
 * FASE 8.4 — la MISMA URL del QR (`/activos/:id`) resuelve la interfaz por
 * rol: quien gestiona ve la ficha interna; el CLIENTE, su ficha pública.
 * Nunca se lleva al cliente a la ficha interna (tampoco podría: la API le
 * responde 403 y la RLS no le deja leer columnas internas).
 */
/** FASE 9.1-C — el listado `/activos` también resuelve por rol: el CLIENTE va a SUS activos (vista pública). */
function ActivosPorRol(): ReactNode {
  const { puede } = useSesion();
  if (!puede('activos:leer') && puede('mantenimiento:leer_propio')) return <Navigate to="/portal/mantenimiento/activos" replace />;
  return <PaginaActivos />;
}

function ActivoPorRol(): ReactNode {
  const { puede } = useSesion();
  const { pathname } = useLocation();
  const id = pathname.split('/')[2] ?? '';
  const etiqueta = pathname.endsWith('/etiqueta');
  if (!puede('activos:leer') && puede('mantenimiento:leer_propio')) {
    return <Navigate to={`/portal/mantenimiento/activos/${id}${etiqueta ? '/etiqueta' : ''}`} replace />;
  }
  return etiqueta ? <PaginaEtiquetaActivo /> : <PaginaActivo />;
}

function RequiereSesion({ children }: { children: ReactNode }): ReactNode {
  const { usuario, cargando, errorComprobacion, reintentarComprobacion } = useSesion();
  const ubicacion = useLocation();
  const conectividad = useConectividad();

  //  Mientras se comprueba el token guardado no se decide nada: mandar al
  //  login aquí haría parpadear la pantalla en cada recarga.
  if (cargando) return <Cargando texto="Comprobando la sesión…" />;
  //  Estabilización post-F13: el servidor no contestó bien (5xx, 429, sin red) — la sesión NO ha terminado. Sin red
  //  se sigue el camino offline de siempre; con red, se explica y se reintenta, sin pedir otra vez la contraseña.
  if (usuario === null && errorComprobacion !== null && conectividad !== 'OFFLINE') {
    return (
      <main className="contenido" style={{ maxWidth: 520, margin: '48px auto', padding: 16 }}>
        <h1>No se ha podido comprobar la sesión</h1>
        <p>Tu sesión sigue abierta, pero el servidor no ha respondido como esperábamos.</p>
        <ErrorCaja error={errorComprobacion} reintentar={reintentarComprobacion} />
      </main>
    );
  }
  if (usuario === null) {
    //  FASE 5.7-C C10 — el arranque de la PWA (icono de inicio → `/`,
    //  `start_url` del manifest, SIEMPRE detrás de esta guardia) sin
    //  conexión desde el primer instante nunca completa `GET
    //  /api/auth/me` (`contexto.tsx`): `usuario` se queda en `null` aunque
    //  el dispositivo tenga un perfil offline protegido y vigente. Mandar
    //  siempre a `/entrar` —un formulario que no puede completarse sin
    //  red— era la causa raíz: exigía autenticación de backend para algo
    //  que el PIN local ya autoriza por diseño (§10). `/offline` (misma
    //  guardia offline-aware que ya usaban esas rutas, ver más abajo) sabe
    //  decidir sola entre perfil bloqueado (PIN), perfil ya desbloqueado,
    //  o ningún perfil preparado (mensaje explícito) — nunca convierte el
    //  PIN en autenticación de backend, sólo desbloquea el paquete ya
    //  autorizado en una sincronización anterior.
    //  F14.2: `arranqueSinConexion` deja a `/offline` llevar al OPERARIO con datos a «Mis trabajos».
    if (conectividad === 'OFFLINE') {
      return <Navigate to="/offline" replace state={{ arranqueSinConexion: true }} />;
    }
    return <Navigate to="/entrar" replace state={{ desde: ubicacion.pathname }} />;
  }
  if (usuario.requiereCambioPwd && ubicacion.pathname !== RUTA_CAMBIAR_PASSWORD) {
    return <Navigate to={RUTA_CAMBIAR_PASSWORD} replace />;
  }
  return <>{children}</>;
}

/**
 * Variante de `RequiereSesion` para `/offline*` (FASE 5.6 §13, E2E
 * escenario C). Una recarga totalmente sin conexión nunca completa
 * `GET /api/auth/me` (`contexto.tsx`) — `usuario` queda `null` aunque el
 * dispositivo tenga un perfil offline protegido y con TTL vigente. Mandar
 * a `/entrar` en ese caso sería exigir red para algo que el PIN local ya
 * autoriza por diseño (§10: "el PIN permite lectura/operativa offline
 * conforme al paquete existente") — así que, a diferencia de
 * `RequiereSesion`, esta guardia NUNCA redirige por `usuario === null`:
 * dejar pasar siempre a `PuertaOfflineSegura` es justo lo que la hace
 * fiable (FASE 5.7-C C10 — antes comprobaba aquí mismo `ultimoPerfilId()
 * === null` como atajo para decidir si merecía la pena intentarlo, pero
 * esa clave suelta de `localStorage` es exactamente la misma que C09 ya
 * demostró frágil ante limpiezas de almacenamiento — un falso negativo
 * aquí rebotaba a `/entrar` sin llegar nunca a el fallback de C09, que
 * SÍ mira el directorio real en IndexedDB). `PuertaOfflineSegura` ya sabe
 * mostrar su propio mensaje explícito cuando de verdad no hay ningún
 * perfil que ofrecer.
 */
function RequiereSesionOAccesoOffline({ children }: { children: ReactNode }): ReactNode {
  const { usuario, cargando } = useSesion();
  const ubicacion = useLocation();

  if (cargando) return <Cargando texto="Comprobando la sesión…" />;
  if (usuario !== null && usuario.requiereCambioPwd && ubicacion.pathname !== RUTA_CAMBIAR_PASSWORD) {
    return <Navigate to={RUTA_CAMBIAR_PASSWORD} replace />;
  }
  return <>{children}</>;
}

/**
 * F14.2 — `/mis-ots*` con conexión es la pantalla de siempre (cliente u operario, con sesión). SIN conexión y sin
 * haber podido validar sesión (recarga o arranque sin red), el operario sigue en SU jornada con el PIN local, sin
 * pasar por `/offline` (`MisTrabajosSinSesion`): el trabajo normal nunca exige navegar a otra parte. Con red y sin
 * sesión, al login, como siempre.
 */
function RequiereSesionOTrabajoSinConexion({ detalle, children }: { detalle: boolean; children: ReactNode }): ReactNode {
  const { usuario, cargando } = useSesion();
  const conectividad = useConectividad();
  if (usuario === null && !cargando && conectividad === 'OFFLINE') return <MisTrabajosSinSesion detalle={detalle} />;
  return <RequiereSesion>{children}</RequiereSesion>;
}

/** F14.3 — igual que `RequiereSesionOTrabajoSinConexion`, para el técnico de mantenimiento («Mis partes»). */
function RequiereSesionOTrabajoMntSinConexion({ detalle, children }: { detalle: boolean; children: ReactNode }): ReactNode {
  const { usuario, cargando } = useSesion();
  const conectividad = useConectividad();
  if (usuario === null && !cargando && conectividad === 'OFFLINE') return <MisPartesSinSesion detalle={detalle} />;
  return <RequiereSesion>{children}</RequiereSesion>;
}

export function App(): ReactNode {
  return (
    <>
      {/*
        FASE 4.5: fuera de `<Routes>` a propósito — se ve igual en
        `/entrar` (sin `Shell`) que en cualquier pantalla autenticada.
      */}
      <EstadoConexion />
      <Routes>
      <Route path="/entrar" element={<PaginaLogin />} />

      <Route
        path={RUTA_CAMBIAR_PASSWORD}
        element={
          <RequiereSesion>
            <PaginaCambiarPassword />
          </RequiereSesion>
        }
      />

      <Route
        path="/"
        element={
          <RequiereSesion>
            <PaginaInicio />
          </RequiereSesion>
        }
      />

      <Route
        path="/clientes"
        element={
          <RequiereSesion>
            <PaginaClientes />
          </RequiereSesion>
        }
      />
      <Route
        path="/clientes/:idCliente"
        element={
          <RequiereSesion>
            <PaginaCliente />
          </RequiereSesion>
        }
      />
      <Route
        path="/clientes/:idCliente/portal-indicadores"
        element={
          <RequiereSesion>
            <PaginaIndicadoresPortal />
          </RequiereSesion>
        }
      />
      <Route
        path="/clientes/:idCliente/tipos-limpieza"
        element={
          <RequiereSesion>
            <PaginaTiposLimpieza />
          </RequiereSesion>
        }
      />
      <Route
        path="/clientes/:idCliente/tipos-limpieza/:idTipo"
        element={
          <RequiereSesion>
            <DisposicionTipoLimpieza />
          </RequiereSesion>
        }
      >
        <Route index element={<TabDatosTipoLimpieza />} />
        <Route path="datos" element={<TabDatosTipoLimpieza />} />
        <Route path="requisitos" element={<TabRequisitosTipoLimpieza />} />
      </Route>

      <Route
        path="/centros/:idCentro"
        element={
          <RequiereSesion>
            <DisposicionCentro />
          </RequiereSesion>
        }
      >
        <Route index element={<ResumenCentro />} />
        <Route path="planificacion" element={<PaginaPlanificacion />} />
        <Route path="importaciones" element={<PaginaImportaciones />} />
        <Route path="importaciones/nueva" element={<PaginaNuevaImportacion />} />
        <Route path="ots" element={<PaginaOts />} />
        <Route path="capacidad" element={<PaginaCapacidadCarga />} />
        <Route path="calendario" element={<PaginaCalendario />} />
        <Route path="ubicaciones" element={<PaginaUbicaciones />} />
      </Route>

      <Route
        path="/planes"
        element={
          <RequiereSesion>
            <PaginaPlanes />
          </RequiereSesion>
        }
      />
      <Route
        path="/planes/:idPlan"
        element={
          <RequiereSesion>
            <PaginaEditorPlan />
          </RequiereSesion>
        }
      />

      <Route
        path="/planner"
        element={
          <RequiereSesion>
            <PaginaPlanner />
          </RequiereSesion>
        }
      />

      <Route
        path="/ots"
        element={
          <RequiereSesion>
            <PaginaOtsGlobal />
          </RequiereSesion>
        }
      />
      <Route
        path="/ots/:idOt"
        element={
          <RequiereSesion>
            <PaginaOt />
          </RequiereSesion>
        }
      />

      <Route
        path="/operarios"
        element={
          <RequiereSesion>
            <PaginaOperarios />
          </RequiereSesion>
        }
      />
      <Route
        path="/operarios/:id"
        element={
          <RequiereSesion>
            <DisposicionOperario />
          </RequiereSesion>
        }
      >
        <Route index element={<TabDatosOperario />} />
        <Route path="datos" element={<TabDatosOperario />} />
        <Route path="centros" element={<TabCentrosOperario />} />
        <Route path="polivalencias" element={<TabPolivalenciasOperario />} />
        <Route path="aptitud" element={<TabAptitudOperario />} />
        <Route path="disponibilidad" element={<TabDisponibilidadOperario />} />
      </Route>

      <Route
        path="/polivalencias"
        element={
          <RequiereSesion>
            <PaginaPolivalencias />
          </RequiereSesion>
        }
      />

      <Route
        path="/documentos"
        element={
          <RequiereSesion>
            <PaginaDocumentos />
          </RequiereSesion>
        }
      />

      <Route
        path="/plantillas-calendario"
        element={
          <RequiereSesion>
            <PaginaPlantillasCalendario />
          </RequiereSesion>
        }
      />

      {/* FASE 8.1 — Mantenimiento: activos. `/activos/:id` es la ruta del QR (login obligatorio). */}
      <Route
        path="/activos"
        element={
          <RequiereSesion>
            <ActivosPorRol />
          </RequiereSesion>
        }
      />
      <Route
        path="/activos/:id"
        element={
          <RequiereSesion>
            <ActivoPorRol />
          </RequiereSesion>
        }
      />
      <Route
        path="/activos/:id/etiqueta"
        element={
          <RequiereSesion>
            <ActivoPorRol />
          </RequiereSesion>
        }
      />
      <Route
        path="/tipos-activo"
        element={
          <RequiereSesion>
            <PaginaTiposActivo />
          </RequiereSesion>
        }
      />

      {/* FASE 8.2 — Mantenimiento: partes de trabajo e incidencias (≠ Calidad). */}
      <Route
        path="/mantenimiento/partes"
        element={
          <RequiereSesion>
            <PaginaPartes />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/partes/nuevo"
        element={
          <RequiereSesion>
            <PaginaNuevoParte />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/partes/:id"
        element={
          <RequiereSesion>
            <PaginaParte />
          </RequiereSesion>
        }
      />
      {/* F14.3 — la misma pantalla del técnico con y sin conexión (sin sesión y sin red: PIN local). */}
      <Route
        path="/mantenimiento/mis-partes"
        element={
          <RequiereSesionOTrabajoMntSinConexion detalle={false}>
            <PaginaMisPartes />
          </RequiereSesionOTrabajoMntSinConexion>
        }
      />
      <Route
        path="/mantenimiento/mis-partes/:id"
        element={
          <RequiereSesionOTrabajoMntSinConexion detalle>
            <PaginaParteCampo />
          </RequiereSesionOTrabajoMntSinConexion>
        }
      />
      {/* FASE 9.1 — Inventario (transversal): maestros, artículos y almacenes. FASE 9.2 — stock (ledger). */}
      {([
        ['/inventario/articulos', <PaginaArticulos />],
        ['/inventario/articulos/:id', <PaginaArticulo />],
        ['/inventario/almacenes', <PaginaAlmacenes />],
        ['/inventario/almacenes/:id', <PaginaAlmacen />],
        ['/inventario/maestros', <PaginaMaestrosInventario />],
        //  F13.8: importaciones maestras (unidades, familias, almacenes, artículos), un solo flujo.
        ['/inventario/importaciones', <PaginaImportacionesMaestras />],
        ['/inventario/importaciones/nueva', <PaginaNuevaImportacionMaestra />],
        ['/inventario/escaneos-pendientes', <PaginaEscaneosPendientes />],
        //  F13.5-B: exportaciones administrativas (stock, movimientos, consumos → SAP/Sage/otro).
        ['/inventario/exportaciones', <PaginaExportaciones />],
        ['/inventario/exportaciones/nueva', <PaginaNuevaExportacion />],
        ['/inventario/exportaciones/:id', <PaginaExportacion />],
        //  F13.6: stock histórico (a una fecha), kárdex y cierres administrativos (foto mensual versionada, conciliación).
        ['/inventario/stock-historico', <PaginaStockHistorico />],
        ['/inventario/kardex', <PaginaKardex />],
        ['/inventario/cierres', <PaginaCierres />],
        ['/inventario/cierres/:id', <PaginaCierre />],
        ['/inventario/cierres/:id/conciliaciones/:numero', <PaginaConciliacion />],
        ['/inventario/almacenes-moviles', <PaginaAlmacenesMoviles />],
        ['/inventario/almacenes-moviles/:id', <PaginaAlmacenMovil />],
        ['/inventario/mi-furgoneta', <PaginaMiFurgoneta />],
        ['/inventario/importaciones/:id', <PaginaImportacionMaestra />],
        ['/inventario/stock', <PaginaStock />],
        ['/inventario/movimientos', <PaginaMovimientos />],
        ['/inventario/operaciones/nueva', <PaginaNuevaOperacion />],
        ['/inventario/operaciones/:id', <PaginaOperacion />],
        ['/inventario/inventarios', <PaginaInventariosFisicos />],
        ['/inventario/inventarios/:id', <PaginaInventarioFisico />],
        //  FASE 9.4-B: los listados de lotes y series viven en Stock; las rutas antiguas redirigen.
        ['/inventario/lotes', <Navigate to="/inventario/stock?tab=lotes" replace />],
        ['/inventario/lotes/:id', <PaginaLote />],
        ['/inventario/series', <Navigate to="/inventario/stock?tab=series" replace />],
        ['/inventario/series/:id', <PaginaSerie />],
      ] as const).map(([ruta, pagina]) => (
        <Route key={ruta} path={ruta} element={<RequiereSesion>{pagina}</RequiereSesion>} />
      ))}

      {/* FASE 8.4 — Área cliente: Mantenimiento (sólo DTO público, pantallas propias). */}
      {([
        ['/portal/mantenimiento', <PaginaPortalMantenimiento />],
        ['/portal/mantenimiento/incidencias', <PaginaPortalIncidenciasMnt />],
        ['/portal/mantenimiento/incidencias/nueva', <PaginaPortalNuevaIncidenciaMnt />],
        ['/portal/mantenimiento/incidencias/:id', <PaginaPortalIncidenciaMnt />],
        ['/portal/mantenimiento/partes', <PaginaPortalPartes />],
        ['/portal/mantenimiento/partes/:id', <PaginaPortalParte />],
        ['/portal/mantenimiento/activos', <PaginaPortalActivos />],
        ['/portal/mantenimiento/activos/:id', <PaginaPortalActivo />],
        ['/portal/mantenimiento/activos/:id/etiqueta', <PaginaPortalEtiquetaActivo />],
        ['/portal/mantenimiento/proximos', <PaginaPortalProximos />],
        //  F12.4 — indicadores públicos de mantenimiento (sólo lo suyo y visible).
        ['/portal/mantenimiento/indicadores', <PaginaPortalIndicadoresMnt />],
      ] as const).map(([ruta, pagina]) => (
        <Route key={ruta} path={ruta} element={<RequiereSesion>{pagina}</RequiereSesion>} />
      ))}

      {/* FASE 8.3 — Mantenimiento: planes preventivos y Planner (PT + proyecciones). */}
      <Route
        path="/mantenimiento/preventivos"
        element={
          <RequiereSesion>
            <PaginaPreventivos />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/preventivos/:id"
        element={
          <RequiereSesion>
            <PaginaPreventivo />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/planner"
        element={
          <RequiereSesion>
            <PaginaPlannerMantenimiento />
          </RequiereSesion>
        }
      />
      {/* F12.4 — indicadores de Mantenimiento (gestión); los del cliente van en su área. */}
      <Route
        path="/mantenimiento/indicadores"
        element={
          <RequiereSesion>
            <PaginaIndicadoresMantenimiento />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/incidencias"
        element={
          <RequiereSesion>
            <PaginaIncidenciasMnt />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/incidencias/nueva"
        element={
          <RequiereSesion>
            <PaginaNuevaIncidenciaMnt />
          </RequiereSesion>
        }
      />
      <Route
        path="/mantenimiento/incidencias/:id"
        element={
          <RequiereSesion>
            <PaginaIncidenciaMnt />
          </RequiereSesion>
        }
      />

      {/* FASE 7.1 — Calidad (gestión) y parte corto de incidencia (operaria). */}
      <Route
        path="/calidad"
        element={
          <RequiereSesion>
            <PaginaCalidad />
          </RequiereSesion>
        }
      />
      <Route
        path="/calidad/incidencias/:id"
        element={
          <RequiereSesion>
            <PaginaIncidencia />
          </RequiereSesion>
        }
      />
      <Route
        path="/calidad/no-conformidades/:id"
        element={
          <RequiereSesion>
            <PaginaNcCalidad />
          </RequiereSesion>
        }
      />
      <Route
        path="/mis-acciones"
        element={
          <RequiereSesion>
            <PaginaMisAcciones />
          </RequiereSesion>
        }
      />
      <Route
        path="/reportar"
        element={
          <RequiereSesion>
            <PaginaReportarIncidencia />
          </RequiereSesion>
        }
      />

      {/* Portal cliente v2 — calendario de sólo lectura (las vistas del Planner, sin ninguna acción). */}
      <Route
        path="/portal/planner"
        element={
          <RequiereSesion>
            <PaginaPortalPlanner />
          </RequiereSesion>
        }
      />

      {/* FASE 7.3 — Calidad del portal cliente (sólo lectura, DTO público). */}
      <Route
        path="/portal/calidad"
        element={
          <RequiereSesion>
            <PaginaPortalCalidad />
          </RequiereSesion>
        }
      />
      <Route
        path="/portal/calidad/incidencias/:id"
        element={
          <RequiereSesion>
            <PaginaPortalIncidencia />
          </RequiereSesion>
        }
      />
      {/* F11.3 — el cliente abre una NC desde Calidad (antes que `:id`). */}
      <Route
        path="/portal/calidad/no-conformidades/nueva"
        element={
          <RequiereSesion>
            <PaginaPortalNuevaNc />
          </RequiereSesion>
        }
      />
      <Route
        path="/portal/calidad/no-conformidades/:id"
        element={
          <RequiereSesion>
            <PaginaPortalNc />
          </RequiereSesion>
        }
      />

      <Route
        path="/mis-ots"
        element={
          <RequiereSesionOTrabajoSinConexion detalle={false}>
            <RutaMisOts />
          </RequiereSesionOTrabajoSinConexion>
        }
      />
      <Route
        path="/mis-ots/:idOt"
        element={
          <RequiereSesionOTrabajoSinConexion detalle>
            <RutaMiOt />
          </RequiereSesionOTrabajoSinConexion>
        }
      />

      <Route
        path="/offline"
        element={
          <RequiereSesionOAccesoOffline>
            <PuertaOfflineSegura>
              <PaginaOffline />
            </PuertaOfflineSegura>
          </RequiereSesionOAccesoOffline>
        }
      />
      <Route
        path="/offline/revision"
        element={
          <RequiereSesionOAccesoOffline>
            <PuertaOfflineSegura>
              <PaginaOfflineRevision />
            </PuertaOfflineSegura>
          </RequiereSesionOAccesoOffline>
        }
      />
      <Route
        path="/offline/:idOt"
        element={
          <RequiereSesionOAccesoOffline>
            <PuertaOfflineSegura>
              <PaginaOfflineOt />
            </PuertaOfflineSegura>
          </RequiereSesionOAccesoOffline>
        }
      />

      <Route
        path="/usuarios"
        element={
          <RequiereSesion>
            <PaginaUsuarios />
          </RequiereSesion>
        }
      />
      <Route
        path="/usuarios/:id"
        element={
          <RequiereSesion>
            <PaginaUsuario />
          </RequiereSesion>
        }
      />

      <Route path="*" element={<Navigate to="/clientes" replace />} />
      </Routes>
    </>
  );
}
